'use client';

import { useRouter } from 'next/navigation';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';

import { checkoutUrlBuilder } from '../../../checkout/builders';
import type { CourseClipUnlock } from '../../../series/shared/player/ClipAccessGate';
import type { CourseClipPurchaseData } from './courseClipPurchaseData';

/**
 * Turns the server-built {@link CourseClipPurchaseData} into the paywall's
 * {@link CourseClipUnlock} descriptor — attaching the `onPurchase` handler that
 * soft-navigates to the SAME course checkout the banner's Purchase button builds
 * (`checkoutUrlBuilder().course(...)`), returning to the course page on success.
 * On success the shared `CoursePurchaseProvider` re-resolves ownership on the
 * `course` event and the clip unlocks with no reload. `undefined` in →
 * `undefined` out (subscribe-only paywall).
 */
export function useCourseClipUnlock(
  data: CourseClipPurchaseData | undefined,
  bioContext: BioContext,
): CourseClipUnlock | undefined {
  const router = useRouter();
  if (!data) return undefined;

  const courseUrl = bioUrl(bioContext).course(data.courseSlug);
  return {
    priceLabel: data.priceLabel,
    originalPriceLabel: data.originalPriceLabel,
    onPurchase: () => {
      const url = checkoutUrlBuilder()
        .course(intent =>
          intent.urlCode(bioContext.urlCode).slug(data.courseSlug),
        )
        .onSuccess(hooks => hooks.redirectTo(courseUrl))
        .url();
      router.push(url);
    },
  };
}
